import type { Editor } from '@tiptap/react';
import type { EditorToolbarItemProps } from '../types';

import { useRef, useCallback } from 'react';

import { toast } from 'src/components/snackbar';

import { editorClasses } from '../classes';
import { ToolbarItem } from './toolbar-item';

// ----------------------------------------------------------------------

const MAX_IMAGE_BYTES = 5 * 1024 * 1024; // 5MB — se GmailService/Gmail-gränsen på ~25MB per mail

type ImageUploadBlockProps = Pick<EditorToolbarItemProps, 'icon'> & {
  editor: Editor;
};

export function ImageUploadBlock({ editor, icon }: ImageUploadBlockProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleClick = useCallback(() => {
    inputRef.current?.click();
  }, []);

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      event.target.value = '';
      if (!file) return;

      if (!file.type.startsWith('image/')) {
        toast.error('Filen måste vara en bild');
        return;
      }
      if (file.size > MAX_IMAGE_BYTES) {
        toast.error('Bilden är för stor (max 5 MB)');
        return;
      }

      const reader = new FileReader();
      reader.onload = () => {
        const dataUrl = reader.result;
        if (typeof dataUrl === 'string') {
          editor.chain().focus().setImage({ src: dataUrl, alt: file.name }).run();
        }
      };
      reader.onerror = () => toast.error('Kunde inte läsa bilden');
      reader.readAsDataURL(file);
    },
    [editor]
  );

  return (
    <>
      <ToolbarItem
        aria-label="Ladda upp bild"
        className={editorClasses.toolbar.uploadImage}
        onClick={handleClick}
        icon={icon}
      />
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        hidden
        onChange={handleChange}
      />
    </>
  );
}
